import { SERVICE_NAME } from '@/app/app.config'
import CONFIG from '@/constants/config'
import {
  CLAIM_FAULT_TYPE,
  ORDER_CLAIM_TYPE,
  ORDER_TYPE,
  PRODUCT_ASSORTMENTS,
} from '@/constants/data'
import { LOCAL_STORAGE_KEYS } from '@/constants/keys'
import { dayjsInstance } from '@/lib/Dayjs'
import { ICartItemWithPriceModel } from '@/types/interfaces/cart'
import { IOrderDetailModel, IOrderPendingWithCartModel } from '@/types/interfaces/order'
import { IOrderOptionFlatModel } from '@/types/interfaces/orderOption'
import { IProduct } from '@/types/interfaces/products'
import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage'
import { objectToParams } from '@/utils/queryString'

// Window 타입 확장 (이미 정의된 경우 무시)
interface GTMWindow extends Window {
  dataLayer?: Record<string, unknown>[]
}

// Analytics 이벤트 상수 정의
export const ANALYTICS_EVENT = {
  // 🔐 사용자 인증 관련
  SIGN_UP: 'sign_up', // 회원가입 완료
  LOGIN: 'login', // 로그인 성공

  // 🛒 상품 및 장바구니 관련
  VIEW_ITEM_LIST: 'view_item_list', // 상품 목록 조회
  VIEW_ITEM: 'view_item', // 상품 상세 조회
  SELECT_ITEM: 'select_item', // 상품 클릭
  ADD_TO_CART: 'add_to_cart', // 장바구니 추가
  VIEW_CART: 'view_cart', // 장바구니 조회
  REMOVE_FROM_CART: 'remove_from_cart', // 장바구니 제거

  // 💳 주문 및 결제 관련
  BEGIN_CHECKOUT: 'begin_checkout', // 결제 시작
  ADD_PAYMENT_INFO: 'add_payment_info', // 결제수단 선택
  ADD_SHIPPING_INFO: 'add_shipping_info', // 배송 정보 입력
  PURCHASE: 'purchase', // 주문 완료

  // 👤 마이페이지 관련
  REFUND: 'refund', // 환불(주문취소)
  REQUEST_CLAIM: 'request_claim', // 교환/반품 요청
  EDIT_PROFILE: 'edit_profile', // 개인정보 수정
  WRITE_REVIEW: 'write_review', // 리뷰 작성

  // 🔍 검색
  SEARCH: 'search', // 상품 검색
} as const

// GTM dataLayer로 이벤트 전송
export const trackEvent = (
  eventName: string,
  parameters: Record<string, unknown> = {},
  ecommerce: Record<string, unknown> = {}
) => {
  if (typeof window !== 'undefined') {
    const payload = {
      event: eventName,
      ...parameters,
      ecommerce,
    }

    const gtmWindow = window as GTMWindow
    if (gtmWindow.dataLayer) {
      gtmWindow.dataLayer.push({ ecommerce: null }) // 이전 이벤트 초기화
      gtmWindow.dataLayer.push(payload)
    } else if (!CONFIG.isProd) {
      console.log('GTM 이벤트 전송:', payload)
    }
  }
}

const parseProductPrice = (value: string | null | undefined) => {
  const price = Number(value?.replace(/[^\d.-]/g, ''))
  return Number.isFinite(price) ? price : 0
}

// 상품 정보를 GA 아이템 형식으로 변환
const formatProductItem = ({
  product,
  index,
  quantity,
  price,
}: {
  product: IProduct
  index?: number
  quantity?: number
  price?: number
}) => {
  return {
    item_id: product.id.toString(),
    item_name: product.name,
    item_category: product.category || 'Unknown Category',
    item_brand: SERVICE_NAME,
    price: price ?? parseProductPrice(product.mainPrice),
    quantity,
    index,
    custom_parameters: {
      code: product.code,
      state: product.state,
      place: product.place,
      schedule: product.scheduleValue,
      time_required: product.timeRequired,
      start_time: product.startTime,
      view_count: product.viewCount,
      like_count: product.likeCount,
      keywords: product.keywords,
      children_price: parseProductPrice(product.childrenPrice),
      infants_price: parseProductPrice(product.infantsPrice),
    },
  }
}

// 주문 상품정보를 GA 아이템 형식으로 변환
const formatOrderItems = ({ order }: { order: IOrderDetailModel }) => {
  return order.shopOrderItems.flatMap(item => {
    return item.shopOrderOptions.map(option => ({
      item_id: item.shopProductId.toString(),
      item_name: item.name,
      item_category: item.category || 'Unknown Category', // 메인 카테고리
      item_category2: item.subCategory || 'Unknown Subcategory', // 하위 카테고리
      item_brand: SERVICE_NAME,
      item_variant: PRODUCT_ASSORTMENTS[item.assortment] || '일반',
      price: item.sellAmount + (option.addAmount || 0),
      quantity: option.quantity,
      // 커스텀 매개변수
      custom_parameters: {
        option_name: option.name,
        is_consulting: order.type === ORDER_TYPE.CONSULT,
        assortment: item.assortment,
        delivery_type: item.deliveryType,
        delivery_fee_type: item.deliveryFeeType,
        tax_type: item.taxType,
        is_base: item.isBase,
      },
    }))
  })
}

// 주문 커스텀 파라미터
const getOrderCustomParameters = ({
  order,
}: {
  order: IOrderDetailModel | IOrderPendingWithCartModel
}) => {
  return {
    is_consulting: order.type === ORDER_TYPE.CONSULT,
    tax_free: order.taxType === 'NON_TAXABLE', // 비과세 여부
    addr_type: order.addrType, // 주소 유형
    total_amount: order.totalAmount, // 총 주문 금액 (포인트 사용 전)
    use_point: order.usePoint, // 사용한 포인트
    total_payment_amount: order.totalPaymentAmount, // 실제 결제 금액
    total_delivery_fee: order.totalDeliveryFee, // 배송비
    payment_method: order.paymentHistory?.payMethod, // 결제 수단
  }
}

// 장바구니 상품 정보를 GA 아이템 형식으로 변환
const formatCartItems = ({ cartItems }: { cartItems: ICartItemWithPriceModel[] }) => {
  return cartItems.flatMap(({ shopProduct: item, shopCartOptions }) => {
    return shopCartOptions.map(option => ({
      item_id: item.id.toString(),
      item_name: item.name,
      price: item.sellAmount + option.shopProductOption.addAmount || 0,
      quantity: option.quantity,
      custom_parameters: {
        is_consulting: item.isConsulting,
        assortment: item.assortment,
        option_name: option.shopProductOption.name,
        delivery_fee_type: item.deliveryFeeType,
        tax_type: item.taxType,
        is_base: item.isBase,
      },
    }))
  })
}

// 상품 목록 조회 이벤트
export const trackViewItemList = ({
  products,
  listPath,
  listName,
  page,
  totalItems,
  listParams,
}: {
  products: IProduct[]
  listPath: string
  listName: string
  page: number
  totalItems: number
  listParams?: Record<string, unknown>
}) => {
  const paramsString = listParams ? decodeURIComponent(objectToParams(listParams).toString()) : ''

  trackEvent(
    ANALYTICS_EVENT.VIEW_ITEM_LIST,
    {
      page_number: page,
      total_items: totalItems,
      list_params: paramsString || undefined,
    },
    {
      item_list_id: listPath.replace(/^\/?/, '').replace(/\//g, '_'),
      item_list_name: listName,
      items: products.map((product, index) => formatProductItem({ product, index })),
    }
  )
}

// 상품 상세 조회 이벤트
export const trackViewItem = ({ product }: { product: IProduct }) => {
  trackEvent(
    ANALYTICS_EVENT.VIEW_ITEM,
    {},
    {
      currency: 'KRW',
      value: parseProductPrice(product.mainPrice),
      items: [formatProductItem({ product })],
    }
  )
}

// 상품 선택 이벤트
export const trackSelectItem = ({
  product,
  listPath,
  listName,
  index,
}: {
  product: IProduct
  listPath: string
  listName: string
  index?: number
}) => {
  trackEvent(
    ANALYTICS_EVENT.SELECT_ITEM,
    {},
    {
      item_list_id: listPath.replace(/^\/?/, '').replace(/\//g, '_'),
      item_list_name: listName,
      items: [formatProductItem({ product, index: index != null ? index + 1 : undefined })],
    }
  )
}

// 장바구니 추가 이벤트
export const trackAddToCart = ({
  product,
  options,
  totalAmount,
}: {
  product: IProduct
  options: Array<{ name: string; quantity: number; price: number }>
  totalAmount: number
}) => {
  trackEvent(
    ANALYTICS_EVENT.ADD_TO_CART,
    {},
    {
      currency: 'KRW',
      value: totalAmount,
      items: options.map(option => {
        const item = formatProductItem({
          product,
          quantity: option.quantity,
          price: option.price,
        })
        return {
          ...item,
          custom_parameters: {
            option_name: option.name,
            ...item.custom_parameters,
          },
        }
      }),
    }
  )
}

// 장바구니 전용 조회 이벤트
export const trackViewCart = ({
  cartItems,
  totalValue,
}: {
  cartItems: ICartItemWithPriceModel[]
  totalValue: number
}) => {
  trackEvent(
    ANALYTICS_EVENT.VIEW_CART,
    {
      num_items: cartItems.length,
    },
    {
      currency: 'KRW',
      value: totalValue,
      items: formatCartItems({ cartItems }),
    }
  )
}

// 장바구니 제거 이벤트
export const trackRemoveFromCart = ({
  cartItems,
  totalValue,
}: {
  cartItems: ICartItemWithPriceModel[]
  totalValue: number
}) => {
  trackEvent(
    ANALYTICS_EVENT.REMOVE_FROM_CART,
    {},
    {
      currency: 'KRW',
      value: totalValue,
      num_items: cartItems.length,
      items: formatCartItems({ cartItems }),
    }
  )
}

// 결제 시작 이벤트
export const trackBeginCheckout = ({ order }: { order: IOrderPendingWithCartModel }) => {
  trackEvent(
    ANALYTICS_EVENT.BEGIN_CHECKOUT,
    {
      ...getOrderCustomParameters({ order }),
    },
    {
      transaction_id: order.id.toString(),
      currency: 'KRW',
      value: order.totalPaymentAmount,
      items: formatOrderItems({
        order: {
          ...order,
          shopOrderItems: order.originalShopOrderItems,
        },
      }),
    }
  )
}

// 배송 정보 입력 이벤트
export const trackAddShippingInfo = ({
  order,
  shipping_tier = 'standard',
}: {
  order: IOrderPendingWithCartModel
  shipping_tier: 'standard' | 'direct' | 'pickup' // 일반배송, 직배송, 현장수령
}) => {
  trackEvent(
    ANALYTICS_EVENT.ADD_SHIPPING_INFO,
    {
      ...getOrderCustomParameters({ order }),
    },
    {
      transaction_id: order.id.toString(),
      currency: 'KRW',
      value: order.totalPaymentAmount,
      shipping_tier,
      items: formatOrderItems({
        order: {
          ...order,
          shopOrderItems: order.originalShopOrderItems,
        },
      }),
    }
  )
}

// 결제 수단 선택 이벤트
export const trackAddPaymentInfo = ({
  order,
  paymentType,
}: {
  order: IOrderPendingWithCartModel
  paymentType: 'card' | 'vbank' | 'pay' | 'billing'
}) => {
  trackEvent(
    ANALYTICS_EVENT.ADD_PAYMENT_INFO,
    {
      ...getOrderCustomParameters({ order }),
    },
    {
      transaction_id: order.id.toString(),
      currency: 'KRW',
      value: order.totalPaymentAmount,
      payment_type: paymentType,
      items: formatOrderItems({
        order: {
          ...order,
          shopOrderItems: order.originalShopOrderItems,
        },
      }),
    }
  )
}

// 구매 완료 이벤트 (중복 방지 로직 포함)
export const trackPurchase = ({ order }: { order: IOrderDetailModel }) => {
  if (!order.paymentDate) {
    console.log(`주문 ${order.id}는 결제 완료가 되지않아 이벤트를 추적하지 않습니다.`)
    return
  }

  const now = dayjsInstance()
  const orderDate = dayjsInstance(order.paymentDate)

  // 주문 시간이 10분 이내인지 확인
  if (orderDate.isBefore(now.subtract(10, 'minute'))) {
    console.log(`주문 ${order.id}는 결제후 10분이상 지나 이벤트를 추적하지 않습니다.`)
    return
  }

  // 로컬 스토리지에서 추적된 주문 목록 가져오기
  const trackedPurchases = getLocalStorageItem<
    Array<{
      orderId: string
      timestamp: number
    }>
  >(LOCAL_STORAGE_KEYS.TRACKED_PURCHASES, [])

  // 이미 추적된 주문인지 확인
  const isAlreadyTracked = trackedPurchases.some(item => item.orderId === order.id)
  if (isAlreadyTracked) {
    console.log(`주문 ${order.id}는 이미 추적되었습니다.`)
    return
  }

  // 이벤트 전송
  trackEvent(
    ANALYTICS_EVENT.PURCHASE,
    {
      ...getOrderCustomParameters({ order }),
    },
    {
      transaction_id: order.id.toString(),
      currency: 'KRW',
      value: order.totalPaymentAmount, // 실제 결제 금액
      shipping: order.totalDeliveryFee,
      tax: 0,
      coupon: order.usePoint ? `POINT${order.usePoint}` : null,
      items: formatOrderItems({ order }),
    }
  )

  // 추적 완료 기록 (24시간 동안 유지)
  const newTrackedPurchase = {
    orderId: order.id,
    timestamp: now.valueOf(),
  }

  // 24시간 이전 데이터 정리
  const oneDayAgo = now.subtract(1, 'day').valueOf()
  const filteredPurchases = trackedPurchases.filter(item => item.timestamp > oneDayAgo)
  setLocalStorageItem(LOCAL_STORAGE_KEYS.TRACKED_PURCHASES, [
    ...filteredPurchases,
    newTrackedPurchase,
  ])
}

// 검색 이벤트
export const trackSearch = ({ searchTerm }: { searchTerm: string }) => {
  trackEvent(ANALYTICS_EVENT.SEARCH, {
    search_term: searchTerm,
  })
}

// 사용자 인증 이벤트
export const trackSignUp = ({
  method,
}: {
  method: 'email' | 'google' | 'naver' | 'kakao' | 'apple'
}) => {
  trackEvent(ANALYTICS_EVENT.SIGN_UP, {
    method,
  })
}

// 로그인 이벤트
export const trackLogin = ({
  method,
}: {
  method: 'email' | 'google' | 'naver' | 'kakao' | 'apple'
}) => {
  trackEvent(ANALYTICS_EVENT.LOGIN, {
    method,
  })
}

// 마이페이지 관련 이벤트
export const trackEditProfile = () => {
  trackEvent(ANALYTICS_EVENT.EDIT_PROFILE)
}

// 리뷰 작성 이벤트
export const trackWriteReview = ({
  productId,
  rating,
  imageCount,
}: {
  productId: number
  rating: number
  imageCount: number
}) => {
  trackEvent(ANALYTICS_EVENT.WRITE_REVIEW, {
    product_id: productId,
    rating,
    num_images: imageCount,
  })
}

// 주문 취소 이벤트(환불)
export const trackRefund = ({ order, reason }: { order: IOrderDetailModel; reason: string }) => {
  trackEvent(
    ANALYTICS_EVENT.REFUND,
    {
      ...getOrderCustomParameters({ order }),
      cancel_reason: reason,
    },
    {
      transaction_id: order.id.toString(),
      currency: 'KRW',
      value: order.totalPaymentAmount,
      tax: 0,
      coupon: order.usePoint ? `POINT${order.usePoint}` : null,
      shipping: order.totalDeliveryFee, // 포인트사용전 배송비
      items: formatOrderItems({ order }),
    }
  )
}

// 교환/반품 신청 이벤트
export const trackRequestClaim = ({
  orderOption,
  claimType,
  reason,
  faultType,
}: {
  orderOption: IOrderOptionFlatModel
  claimType: ORDER_CLAIM_TYPE
  reason: string
  faultType: CLAIM_FAULT_TYPE | null
}) => {
  const { shopOrder, shopOrderItem, ...shopOrderOption } = orderOption
  if (!shopOrder || !shopOrderItem) {
    return
  }

  const order = {
    ...shopOrder,
    shopOrderItems: [
      {
        ...shopOrderItem,
        shopOrderOptions: [shopOrderOption],
      },
    ],
    paymentHistory: null,
  }

  // 상품가(배송비 제외)
  const value = (shopOrderItem.sellAmount + shopOrderOption.addAmount) * shopOrderOption.quantity

  trackEvent(
    ANALYTICS_EVENT.REQUEST_CLAIM,
    {
      claim_type: claimType,
      claim_reason: reason,
      claim_fault_type: faultType,
    },
    {
      transaction_id: order.id.toString(),
      currency: 'KRW',
      value,
      items: formatOrderItems({ order }),
    }
  )
}
